import React, { useContext, useState, useMemo } from 'react';
import { Buffer } from 'buffer';
import _ from 'lodash';
import Dropzone from 'react-dropzone';
import { useDropzone } from 'react-dropzone';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { gql, useMutation, useLazyQuery, useSubscription, useQuery } from '@apollo/client';
import {
  Box,
  AccordionSummary,
  AccordionDetails,
  Accordion,
  Grid,
  Button,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  TextField,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSelector } from 'react-redux';

import { selectProject } from '../../../../features/project/projectSlice';
import { selectUser } from '../../../../features/auth/authSlice';
import { useAppDispatch } from '../../../../app/hooks';
import { showSuccessSnackbar, showErrorSnackbar } from '../../../../features/snackbar/snackbarSlice';
import { accessAllowed } from '../../../../features/project/projectHooks';
import { useCreateActivity } from '../../../../features/activity/activityHooks';
import { UnitContext } from '../../../../context/UnitContext';
import TextInput from '../../../common/formControls/TextInput';
import Dropdown from '../../../common/formControls/Dropdown';
import { IDocuments } from '../../../../types/docusign';
import { mergeType } from '../../../../utils/Constants';
import { IMerge, IMergeData } from '../../../../types/merge';
import { IExecutor } from '../../../../types/project';
import { createPdf, getMergeValues, download, envelopeIdRegex, downloadDistributionFiles, pageRotate } from '../../../../utils/Functions';
import CustomDialog from '../../../common/CustomDialog';
import PdfCard from '../../../common/PdfCard';
import { FlexBetween } from '../../../../commonStyles';
import { baseStyle, activeStyle, acceptStyle, rejectStyle } from '../../../../utils/Constants';
import EnvelopeAccordion from './EnvelopeAccordion';
import LoadingLogo from '../../../common/LoadingLogo';
import { DealContext } from '../../../../context/DealContext';
import { IPurchaserInfo } from '../../../../types/CreateDealForm';
import { ILease } from '../../../../types/lease';
import { generateApsReview } from './apsReview';
import FintracQuestions, {
  IFintracAnswers,
  drawFintracRiskAnswers,
  drawFintracRiskScore,
  drawFintracIdType,
  drawFintracFixedAnswers,
} from './FintracQuestions';

// Fintrac identification record merge templates the Client Risk answers are drawn onto
const CORP_FINTRAC_TEMPLATE_ID = '68504e9dac59474baeaa1d9e';
const INDIVIDUAL_FINTRAC_TEMPLATE_ID = '685195fdc8e80504a9fb78b6';

const Envelopes = () => {
  const { setFilteredDeal, filteredDeal, unit, updateDealDocuments, dealUpdateLoading, assignment } = useContext(UnitContext);
  const { setDocument, setPendingDeposits, setPendingOptions } = useContext(DealContext);
  const createActivity = useCreateActivity();
  const storeDispatch = useAppDispatch();
  const project = useSelector(selectProject);
  const user = useSelector(selectUser);

  const [merges, setMerges] = useState<IMerge[]>([]);
  const [selectedMerge, setSelectedMerge] = useState<any[]>([]);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [dialogType, setDialogType] = useState<string>('');
  const [selectedDocument, setSelectedDocument] = useState<IDocuments | null>(null);
  const [executor, setExecutor] = useState<IExecutor | null>(project.executors ? project.executors[0] : null);
  const [title, setTitle] = useState<string>('');
  const [documentType, setDocumentType] = useState<string>('');
  const [envelopeId, setEnvelopeId] = useState<string>('');
  const [fileImage, setFileImage] = useState<any>('');
  const [file, setFile] = useState<any>(null);
  const [auditDate, setAuditDate] = useState<Date | null>(filteredDeal && filteredDeal.auditDate ? filteredDeal.auditDate : null);
  const [documentName, setDocumentName] = useState<string>('');
  const [selectedDocuments, setSelectedDocuments] = useState<IDocuments[]>([]);
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);
  const [fintracAnswers, setFintracAnswers] = useState<IFintracAnswers>({});
  const [addFloorPlan, setAddFloorPlan] = useState<boolean>(false);
  const [lease, setLease] = useState<ILease | null>(null);
  const [apsReviewLoading, setApsReviewLoading] = useState<boolean>(false);
  // Review APS popup: which documents to generate
  const [reviewSelections, setReviewSelections] = useState<{ apsReview: boolean; depositDates: boolean }>({ apsReview: true, depositDates: false });

  window.removeEventListener('message', () => {});

  const { isDragActive, isDragAccept, isDragReject } = useDropzone({
    accept: 'image/jpeg, image/png',
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept]
  );

  useSubscription(UPDATED_DOCUMENT, {
    variables: { projectId: project._id },
    onSubscriptionData: ({
      subscriptionData: {
        data: { updatedDocument },
      },
    }) => {
      const newDocuments = _.cloneDeep(filteredDeal.documents);
      const foundDocument = newDocuments.find((nd: any) => nd._id === updatedDocument._id);
      if (foundDocument) {
        foundDocument.status = updatedDocument.status;
      }
      setFilteredDeal({
        ...filteredDeal,
        documents: newDocuments,
      });
    },
  });

  useQuery(GETLEASE, {
    variables: { filter: { unit: unit._id, active: true } },
    onCompleted: (data) => {
      if (data && data.leaseOne) {
        setLease(data.leaseOne);
      }
    },
  });

  const [getDocument] = useLazyQuery(GETDOCUMENT, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {},
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [getMerges] = useLazyQuery<IMergeData>(MERGETEMPLATES, {
    onCompleted: (data) => {
      setMerges(data.mergeTemplateMany);
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [deleteDepositAmendment, { loading: deleteDocumentLoading }] = useMutation(DELETEDOCUMENT, {
    onCompleted: (data) => {
      let removedDocument = filteredDeal.documents.filter((document: IDocuments) => {
        return data.deleteDepositAmendment._id !== document._id;
      });
      if (
        data.deleteDepositAmendment.type === 'AddPurchasers' ||
        data.deleteDepositAmendment.type === 'DeletePurchasers' ||
        data.deleteDepositAmendment.type === 'AddDeletePurchasers'
      ) {
        setFilteredDeal({
          ...filteredDeal,
          pendingAddPurchasers: [],
          pendingDeletePurchasers: [],
          documents: removedDocument,
        });
      } else if (
        data.deleteDepositAmendment.type === 'Deposit' ||
        data.deleteDepositAmendment.type === 'Options' ||
        data.deleteDepositAmendment.type === 'UnitTransfer'
      ) {
        setFilteredDeal({
          ...filteredDeal,
          pendingOptions: [],
          pendingUnit: null,
          pendingDeposits: [],
          documents: removedDocument,
        });
        setPendingDeposits([]);
        setPendingOptions([]);
      } else {
        setFilteredDeal({
          ...filteredDeal,
          documents: removedDocument,
        });
      }
      storeDispatch(showSuccessSnackbar(`Document has been deleted/voided!`));
      setDocument(null);
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [createDocument, { loading: createDocumentLoading }] = useMutation(CREATEDOCUMENT, {
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar(`Document has been created!`));
      let documentIds = filteredDeal.documents.map((document: IDocuments) => {
        return document._id;
      });
      updateDealDocuments({
        variables: { _id: filteredDeal._id, record: { documents: [...documentIds, data.documentCreateOne._id] } },
      });
      setFilteredDeal({
        ...filteredDeal,
        documents: [...filteredDeal.documents, data.documentCreateOne],
      });
      setTitle('');
      setDocumentType('');
      setEnvelopeId('');
      setFileImage(null);
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [sendDocument, { loading: sendDocumentLoading }] = useMutation(SENDDOCUMENT, {
    onCompleted: (data) => {
      const documentsClone = [...filteredDeal.documents];
      const updatedDocument = documentsClone.find((doc) => doc._id === data.documentCreateEnvelope._id);
      updatedDocument.status = data.documentCreateEnvelope.status;
      updatedDocument.dsEnvelopeId = data.documentCreateEnvelope.dsEnvelopeId;
      setFilteredDeal({
        ...filteredDeal,
        documents: documentsClone,
      });
      createActivity({
        project: project._id,
        user: user._id,
        deal: filteredDeal._id,
        title: `Document Sent!`,
        content: `Document ${data.documentCreateEnvelope.name} has been sent!`,
      });
      storeDispatch(showSuccessSnackbar(`Document has been sent!`));
    },
    onError: (err) => {
      storeDispatch(showErrorSnackbar(err.toString()));
      console.log(err, 'err');
    },
  });

  const [sendDocumentToExecutor, { loading: sendingDocumentToExecutor }] = useMutation(SENDDOCUMENTTOEXECUTOR, {
    onCompleted: (data) => {
      const documentsClone = _.cloneDeep(filteredDeal.documents);
      const updatedDocument = documentsClone.find((doc: any) => doc._id === data.documentSendToExecutor._id);
      updatedDocument.status = data.documentSendToExecutor.status;
      createActivity({
        project: project._id,
        user: user._id,
        deal: filteredDeal._id,
        title: `Document Sent To Executor`,
        content: `Document ${data.documentSendToExecutor.name} has been sent to the Executor ${executor?.name}`,
      });
      storeDispatch(showSuccessSnackbar(`Document has been sent to Executor ${executor?.name}`));
      setExecutor(null);
      setFilteredDeal({
        ...filteredDeal,
        documents: documentsClone,
      });
      setDialogType('');
      setDialogOpen(false);
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  // Audit
  const [updateDealSales] = useMutation(UPDATEDEALINFO, {
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar('Audit Information Updated'));
      createActivity({
        project: project._id,
        user: user._id,
        deal: filteredDeal._id,
        title: `Deal Information`,
        content: `${filteredDeal.auditDate ? `${unit.suite}'s has been audit has been removed` : `${unit.suite}'s has been audited`}`,
      });
      setFilteredDeal({
        ...filteredDeal,
        auditor: filteredDeal.auditor,
        auditDate: filteredDeal.auditDate,
      });
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const handleAudit = (e: any) => {
    if (filteredDeal.auditor) {
      setAuditDate(null);
      updateDealSales({
        variables: {
          _id: filteredDeal._id,
          record: {
            auditor: null,
            auditDate: null,
          },
        },
      });
    } else {
      setAuditDate(new Date());
      updateDealSales({
        variables: {
          _id: filteredDeal._id,
          record: {
            auditor: user._id,
            auditDate: new Date(),
          },
        },
      });
    }
  };

  const openDialog = async (type: string, document: IDocuments | null = null) => {
    if (type === 'create') {
      getMerges({
        variables: {
          filter: {
            OR: [
              {
                AND: [
                  { project: project._id },
                  {
                    OR: [
                      { type: 'Other' },
                      { type: 'AddPurchasers' },
                      { type: 'DeletePurchasers' },
                      { type: 'AddDeletePurchasers' },
                      { type: 'Assignment' },
                      { name: `${unit.suite}` },
                    ],
                  },
                ],
              },
              {
                type: 'Mandatary',
              },
              {
                type: 'Fintrac',
              },
            ],
          },
        },
      });
    }
    if (type === 'execute' || type === 'sendEnvelope' || type === 'deleteDocument') {
      setSelectedDocument(document);
    }
    await setDialogType(type);
    await setDialogOpen(true);
  };

  const setBackgroundColor = (status: string) => {
    if (status === 'Received') {
      return {
        background: 'rgb(230, 184, 183)',
      };
    } else if (status === 'Signed') {
      return {
        background: 'rgb(233 204 255)',
      };
    } else if (status === 'Verified') {
      return {
        background: 'rgb(0, 176, 240)',
      };
    } else if (status === 'Completed') {
      return {
        background: 'rgb(146, 208, 80)',
      };
    } else return;
  };

  const executorList = () => {
    let executorNames = project.executors.map((executor: IExecutor, index: number) => {
      return executor.name;
    });
    return executorNames;
  };

  const handleExecutorDropdown = (event: React.ChangeEvent<{ name?: string; value: string }>) => {
    let executor = project?.executors.find((executor: IExecutor) => executor.name === event.target.value);
    setExecutor(executor!);
  };

  const handleDrop = (acceptedFiles: any) => {
    const file = acceptedFiles[0];
    const fileReader = new FileReader();
    if (file) {
      fileReader.readAsDataURL(file);
    }
    fileReader.onloadend = async () => {
      setFileImage(fileReader.result);
      setFile(acceptedFiles[0]);
      setEnvelopeId('');
    };
  };

  const generateMultiplePdfs = async (selectedMerges: any[]) => {
    const mergedPdf = await PDFDocument.create();

    let documents = [];
    for (const document of selectedMerges) {
      let autoMergeTemplate = [...merges].find((mergeTemplate: IMerge) => mergeTemplate.name === document.name);

      let autoDocument = await fetch(autoMergeTemplate?.getUrl!).then(async (res) => await res.arrayBuffer());
      const documentPdf = await PDFDocument.load(autoDocument);
      let helveticaFont = await documentPdf.embedFont(StandardFonts.Helvetica);

      let pages = documentPdf.getPages();
      await autoMergeTemplate?.mergeFields.forEach(async (mergeField: any) => {
        let mergePages = pages[mergeField.pageNumber];
        let mergeValue = getMergeValues(
          mergeField,
          project,
          unit,
          null,
          assignment ? assignment.assignees : filteredDeal.purchasers,
          filteredDeal.pendingAddPurchasers,
          filteredDeal.pendingDeletePurchasers,
          filteredDeal.deposit,
          filteredDeal.options,
          filteredDeal.realtor[0],
          null,
          null,
          null,
          null,
          filteredDeal,
          [],
          null,
          lease,
          assignment
        );

        let x = mergeField.x;
        let y = mergePages.getHeight() - mergeField.y - 3;

        if (addFloorPlan && unit.floorPlan) {
          let pageType = mergePages.getRotation();

          let pageRotationValue = pageRotate(pageType, mergeField.x, mergeField.y, 1, mergePages.getSize());

          let pageRotation = mergePages.getRotation();
          if (pageRotation.angle === 0) {
            pageRotationValue.y = mergePages.getHeight() - mergeField.y - 3;
          }

          let checkMediaBox = mergePages.getMediaBox();
          x = pageRotationValue.x;
          y = pageRotationValue.y;

          if (checkMediaBox && (checkMediaBox.x !== 0 || checkMediaBox.y !== 0)) {
            x = pageRotationValue.x - Math.abs(checkMediaBox.x);
            y = pageRotationValue.y - Math.abs(checkMediaBox.y);
          }
        }

        mergePages.drawText(mergeValue, {
          x: x,
          y: y,
          size: mergeField.fontSize,
          font: helveticaFont,
          color: rgb(0, 0, 0),
          rotate: mergePages.getRotation(),
        });
      });

      let copiedPages = await mergedPdf.copyPages(documentPdf, documentPdf.getPageIndices());
      copiedPages.forEach((page: any) => mergedPdf.addPage(page));

      const documentDoc: any = await documentPdf.save();

      let mergeSignFields = autoMergeTemplate?.signFields.map((signFields: any, index: number) => {
        return {
          index: signFields.index,
          key: signFields.key,
          pageNumber: signFields.pageNumber,
          x: Math.round(signFields.x),
          y: Math.round(signFields.y),
          title: signFields.title,
        };
      });

      let blob = await new Blob([documentDoc]);
      let file = await new File([blob], `${document.name}.pdf`, { type: 'application/pdf' });

      documents.push({
        file: file,
        name: document.name,
        mergeTemplate: document._id,
        signFields: mergeSignFields,
        isAPS: false,
        type: 'Other',
      });
    }

    let name = documents.map((document: any) => document.name).join(' + ');

    const pdfBytes: any = await mergedPdf.save();

    const blob = new Blob([pdfBytes]);
    const file = new File([blob], `${name}.pdf`, { type: 'application/pdf' });

    const args = {
      status: 'Prepared',
      name: name,
      project: project._id,
      deal: filteredDeal._id,
      // signers,
      isAPS: false,
      type: 'Other',
      mergeTemplate: documents.map((document: any) => document.mergeTemplate),
    };

    createDocument({ variables: { record: args, file: file } });

    setDialogOpen(false);
  };

  const generateFintrac = async (downloadFintrac: boolean, createFintrac: boolean = false) => {
    const mergedPdf = await PDFDocument.create();
    let documents = [];
    let mandatary = merges.find((merge: IMerge) => merge.type === 'Mandatary');
    // Every purchaser on the deal gets a Fintrac / Mandatary
    const fintracPurchasers: IPurchaserInfo[] = filteredDeal.purchasers;
    if (!fintracPurchasers.length) {
      storeDispatch(showErrorSnackbar('There are no purchasers on this deal'));
      return;
    }
    if (!downloadFintrac && !mandatary) {
      storeDispatch(showErrorSnackbar('No Mandatary merge template found'));
      return;
    }
    for (const [index, purchaser] of fintracPurchasers.entries()) {
      // Download Fintrac: each purchaser's identification record. Create Mandatary: each purchaser's Mandatary,
      // saved as a document to be sent out for signing.
      let fintracMerges: IMerge[] = [];
      if (downloadFintrac) {
        // Corporations get the corporate identification record, everyone else the individual one (by template id,
        // falling back to the template name)
        const fintracId = purchaser.corp ? CORP_FINTRAC_TEMPLATE_ID : INDIVIDUAL_FINTRAC_TEMPLATE_ID;
        const fintracName = purchaser.corp ? 'Corp Fintrac - Sales' : 'Fintrac - Sales';
        let fintrac = merges.find((merge: IMerge) => merge._id === fintracId) || merges.find((merge: IMerge) => merge.name === fintracName);
        if (!fintrac) {
          storeDispatch(showErrorSnackbar(`No ${fintracName} merge template found`));
          return;
        }
        fintracMerges.push(fintrac);
      } else {
        fintracMerges.push(mandatary!);
      }

      for (const document of fintracMerges) {
        let autoMergeTemplate = merges.find((mergeTemplate: IMerge) => mergeTemplate._id === document._id);

        let autoDocument = await fetch(autoMergeTemplate?.getUrl!).then(async (res) => await res.arrayBuffer());
        const documentPdf = await PDFDocument.load(autoDocument);
        let helveticaFont = await documentPdf.embedFont(StandardFonts.Helvetica);

        let pages = documentPdf.getPages();
        await autoMergeTemplate?.mergeFields.forEach(async (mergeField: any) => {
          let mergePages = pages[mergeField.pageNumber];
          let mergeValue = getMergeValues(
            mergeField,
            project,
            unit,
            null,
            [purchaser],
            [],
            [],
            filteredDeal.deposit,
            filteredDeal.options,
            filteredDeal.realtor[0],
            null,
            null,
            null,
            null,
            filteredDeal
          );

          mergePages.drawText(mergeValue, {
            x: mergeField.x,
            y: mergePages.getHeight() - mergeField.y - 3,
            size: mergeField.fontSize,
            font: helveticaFont,
            color: rgb(0, 0, 0),
          });
        });

        // Fill in this purchaser's identification record (individual or corporate - each has its own layout)
        if (document._id === CORP_FINTRAC_TEMPLATE_ID || document._id === INDIVIDUAL_FINTRAC_TEMPLATE_ID) {
          const form = document._id === CORP_FINTRAC_TEMPLATE_ID ? 'corp' : 'individual';
          // Page 3 Part 1: X the Client Risk answers from the popup; Part 2: circle each answer's points and total them
          drawFintracRiskAnswers(pages, fintracAnswers[purchaser._id!], form);
          drawFintracRiskScore(pages, fintracAnswers[purchaser._id!], helveticaFont, form);
          // Page 2: no third party; page 4: property type from the unit type
          drawFintracFixedAnswers(pages, unit.type, form, helveticaFont);
          // Page 1 "Type of Identification Document" is only on the individual record
          if (form === 'individual') drawFintracIdType(pages, purchaser.idType, helveticaFont);
        }

        let copiedPages = await mergedPdf.copyPages(documentPdf, documentPdf.getPageIndices());
        copiedPages.forEach((page: any) => mergedPdf.addPage(page));

        const documentDoc: any = await documentPdf.save();

        let mergeSignFields = autoMergeTemplate?.signFields.map((signFields: any, index: number) => {
          return {
            index: signFields.index,
            key: signFields.key,
            pageNumber: signFields.pageNumber,
            x: Math.round(signFields.x),
            y: Math.round(signFields.y),
            title: signFields.title,
          };
        });

        let blob = await new Blob([documentDoc]);
        let file = await new File([blob], `${document.name}.pdf`, { type: 'application/pdf' });

        documents.push({
          file: file,
          name: `${document.name} - P${index + 1}`,
          mergeTemplate: document._id,
          signFields: mergeSignFields,
          isAPS: false,
          type: 'Fintrac',
        });
      }
    }

    if (downloadFintrac) {
      if (createFintrac) {
        let name = '';

        if (fintracPurchasers.length) {
          name += fintracPurchasers.map((purchaser: IPurchaserInfo) => `${purchaser.firstName} ${purchaser.lastName}`).join(' and ');
          name += ' - FINTRAC';
        }

        const pdfBytes: any = await mergedPdf.save();

        const blob = new Blob([pdfBytes]);
        const file = new File([blob], `${name}.pdf`, { type: 'application/pdf' });

        const args = {
          status: 'Completed',
          name: name,
          project: project._id,
          deal: filteredDeal._id,
          unit: unit._id,
          isAPS: false,
          type: 'Fintrac',
          completedAt: new Date(),
          mergeTemplate: documents.map((document: any) => document.mergeTemplate),
        };

        createDocument({ variables: { record: args, file: file } });

        await setDialogOpen(false);
        await setDialogType('');
      } else {
        const pdfFile = await mergedPdf.save();
        download(pdfFile, `Suite ${unit.suite} - Fintrac`, 'application/pdf');
      }
    } else {
      let name = '';

      if (fintracPurchasers.length) {
        name += fintracPurchasers.map((purchaser: IPurchaserInfo) => `${purchaser.firstName} ${purchaser.lastName}`).join(' and ');
        name += ' - Mandatary';
      }

      const pdfBytes: any = await mergedPdf.save();

      const blob = new Blob([pdfBytes]);
      const file = new File([blob], `${name}.pdf`, { type: 'application/pdf' });

      const args = {
        status: 'Prepared',
        name: name,
        project: project._id,
        deal: filteredDeal._id,
        // signers,
        isAPS: false,
        type: 'Fintrac',
        mergeTemplate: documents.map((document: any) => document.mergeTemplate),
      };

      createDocument({ variables: { record: args, file: file } });

      await setDialogOpen(false);
      await setDialogType('');
    }
  };

  // Builds the APS Review summary PDF and saves it as a Prepared document with Sales Rep/Executor sign fields
  const createApsReview = async () => {
    setApsReviewLoading(true);
    try {
      const { file, signFields } = await generateApsReview(project, unit, filteredDeal);

      const args = {
        status: 'Prepared',
        name: 'APS Review',
        project: project._id,
        deal: filteredDeal._id,
        signFields: signFields,
        isAPS: false,
        type: 'Other',
        mergeTemplate: null,
      };

      await createDocument({ variables: { record: args, file: file } });
    } catch (err) {
      console.log(err, 'err');
      storeDispatch(showErrorSnackbar('Unable to generate APS Review'));
    }
    setApsReviewLoading(false);
  };

  // The API builds the Deposit Dates one-pager and adds it to the deal's documents itself
  const [generateDepositDates] = useMutation(GENERATEDEPOSITDATES, {
    onCompleted: (data) => {
      // Functional update so it doesn't clobber an APS Review created just before it in the same run
      setFilteredDeal((current: any) => ({ ...current, documents: [...current.documents, data.generateDepositDates] }));
      storeDispatch(showSuccessSnackbar('Deposit Date Amendment has been created!'));
    },
    onError: (err) => {
      console.log(err, 'err');
      storeDispatch(showErrorSnackbar(err.message || 'Unable to generate the Deposit Date Amendment'));
    },
  });

  const generateReviewDocuments = async () => {
    if (!reviewSelections.apsReview && !reviewSelections.depositDates) {
      storeDispatch(showErrorSnackbar('Select at least one document to generate'));
      return;
    }
    setDialogOpen(false);
    setDialogType('');
    if (reviewSelections.apsReview) await createApsReview();
    if (reviewSelections.depositDates) {
      setApsReviewLoading(true);
      await generateDepositDates({ variables: { dealId: filteredDeal._id } }).catch(() => null);
      setApsReviewLoading(false);
    }
  };

  const dialogContent = () => {
    if (dialogType === 'create') {
      return (
        <Box>
          <Box>
            <strong>If you need to create an amendment for a realtor to sign, please create it on the realtor page.</strong>
          </Box>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Checkbox checked={addFloorPlan} onChange={(e: any) => setAddFloorPlan(!addFloorPlan)} />}
            label={'Add Floor Plan to Amendment'}
          />
          <Box sx={{ mt: 1 }}>
            <Autocomplete
              multiple
              options={merges.map((option: IMerge) => option.name)}
              getOptionLabel={(option: string) => option}
              isOptionEqualToValue={(option, value) => option === value}
              disableClearable={false}
              freeSolo={false}
              value={selectedMerge}
              onChange={(event: any, newValue: any | null) => {
                setSelectedMerge(newValue.map((option: string) => option));
              }}
              renderInput={(params) => <TextField {...params} label="Merge Templates" size="medium" />}
            />
            {selectedMerge.length > 2 ? (
              <Box>
                <TextField
                  sx={{ mt: 2 }}
                  title={'Envelope Name'}
                  name={'unitNumber'}
                  fullWidth
                  value={documentName}
                  label={'Envelope Name'}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDocumentName(e.target.value)}
                />
              </Box>
            ) : null}
          </Box>
        </Box>
      );
    } else if (dialogType === 'execute') {
      return (
        <div>
          <Dropdown
            title={'Executor'}
            menuList={executorList()}
            name={'executors'}
            handleSelect={(e: any) => handleExecutorDropdown(e)}
            value={executor ? executor.name : ''}
          />
        </div>
      );
    } else if (dialogType === 'sendEnvelope' || dialogType === 'deleteDocument') {
      return (
        <div>
          <strong>{selectedDocument?.name!}</strong>
        </div>
      );
    } else if (dialogType === 'upload') {
      return (
        <div>
          <Box sx={{ my: 2 }}>
            <TextInput
              title={'Document Name'}
              id={'title'}
              handleTextInput={(e: any) => setTitle(e.target.value)}
              value={title}
              required={true}
            />
          </Box>
          <Box sx={{ my: 2 }}>
            <TextField
              error={envelopeIdRegex(envelopeId)}
              title={'Envelope ID'}
              label={'Envelope ID'}
              id={'envelope'}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEnvelopeId(e.target.value)}
              value={envelopeId}
              helperText={envelopeIdRegex(envelopeId) ? 'Incorrect Format' : ''}
              fullWidth
            />
          </Box>
          <Box
            sx={{
              mb: 1,
              '& .MuiAccordionSummary-content': {
                display: 'block',
              },
            }}
          >
            <Dropdown
              id={'type'}
              title={'Type'}
              menuList={mergeType}
              name={'type'}
              handleSelect={(e: any) => setDocumentType(e.target.value)}
              value={documentType}
            />
          </Box>
          {!envelopeId ? (
            <div>
              {fileImage ? (
                <div>
                  <Grid item xs={12}>
                    <PdfCard file={fileImage} title={title} id={'0'} handleDelete={() => setFileImage(null)} download={true} />
                  </Grid>
                </div>
              ) : (
                <Dropzone onDrop={handleDrop} accept="application/pdf">
                  {({ getRootProps, getInputProps }) => (
                    <section>
                      <div {...getRootProps({ style })}>
                        <input {...getInputProps()} />
                        <p>Drag and Drop or Upload a Document (.pdf)</p>
                      </div>
                    </section>
                  )}
                </Dropzone>
              )}
            </div>
          ) : null}
        </div>
      );
    } else if (dialogType === 'download') {
      return (
        <div>
          <Box sx={{ my: 2 }}>
            {filteredDeal.documents
              .filter((document: IDocuments) => document.status === 'Completed')
              .map((document: IDocuments, index: number) => {
                return (
                  <Box>
                    <FormControlLabel
                      key={index}
                      control={
                        <Checkbox
                          checked={selectedDocuments.some((selectedDocument: any) => document._id === selectedDocument._id)}
                          onChange={(e: any) => {
                            if (selectedDocuments.some((selectedDocument: any) => document._id === selectedDocument._id)) {
                              setSelectedDocuments(selectedDocuments.filter((selected: any) => selected._id !== document._id));
                            } else {
                              setSelectedDocuments([...selectedDocuments, document!]);
                            }
                          }}
                        />
                      }
                      label={`${document.name}`}
                    />
                  </Box>
                );
              })}
          </Box>
        </div>
      );
    } else if (dialogType === 'reviewAps') {
      return (
        <Box>
          <Box sx={{ mb: 1 }}>Select the documents you would like to generate.</Box>
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={reviewSelections.apsReview}
                  onChange={() => setReviewSelections({ ...reviewSelections, apsReview: !reviewSelections.apsReview })}
                />
              }
              label="APS Review"
            />
          </Box>
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={reviewSelections.depositDates}
                  onChange={() => setReviewSelections({ ...reviewSelections, depositDates: !reviewSelections.depositDates })}
                />
              }
              label="Deposit Date"
            />
          </Box>
        </Box>
      );
    } else if (dialogType === 'fintrac') {
      return (
        <Box>
          <Box sx={{ mt: 2 }}>
            <strong>
              Fintracs and Mandataries are created for every purchaser on this deal.
            </strong>
          </Box>
          {filteredDeal.purchasers.length ? (
            <Box sx={{ mt: 2 }}>
              <strong>Risk Assessment</strong>
              <FintracQuestions purchasers={filteredDeal.purchasers} answers={fintracAnswers} setAnswers={setFintracAnswers} />
            </Box>
          ) : null}
        </Box>
      );
    }
  };

  const handleCloseSuccess = async () => {
    if (dialogType === 'execute') {
      sendToExecutor();
    } else if (dialogType === 'upload') {
      const args = {
        status: 'Completed',
        name: title,
        project: project._id,
        deal: filteredDeal._id,
        isAPS: false,
        dsEnvelopeId: envelopeId ? envelopeId : null,
        type: documentType,
        mergeTemplate: null,
      };
      setDialogOpen(false);
      if (envelopeId && !envelopeIdRegex(envelopeId)) {
        createDocument({ variables: { record: args } });
      } else if (file) {
        createDocument({ variables: { record: args, file: file } });
      }
    } else if (dialogType === 'sendEnvelope') {
      handleSendDocument(selectedDocument!);
    } else if (dialogType === 'deleteDocument') {
      removeDocuments(selectedDocument?._id!, selectedDocument?.type!);
    } else if (dialogType === 'download') {
      let documents = [];
      setDownloadLoading(true);
      setDialogOpen(false);
      setDialogType('');
      documents = await Promise.all(
        selectedDocuments.map(async (document: any) => {
          let blob = await getDocument({
            variables: {
              project: project._id,
              document: document._id,
            },
          }).then(async (resp) => {
            if (resp.data.getDocument) {
              const response = await fetch(resp.data.getDocument.url);
              const blob2 = await response.blob();
              return blob2;
            }
          });

          return {
            pdf: blob ? blob : null,
            name: document.name,
            dsEnvelopeId: document.dsEnvelopeId,
          };
        })
      );

      let files = [
        {
          _id: unit.suite,
          suite: unit.suite,
          documents: documents,
          mortgage: null,
          depositImages: [],
          purchasers: [],
        },
      ];

      await downloadDistributionFiles(files, `${project.name} - Suite ${unit.suite}`);
      setDownloadLoading(false);
    } else if (dialogType === 'reviewAps') {
      await generateReviewDocuments();
    } else if (dialogType === 'fintrac') {
      await generateFintrac(false);
    } else {
      if (selectedMerge.length === 1) {
        let selectedMerges = merges.filter((merge: IMerge) => selectedMerge.some((mergeName: string) => mergeName === merge.name));

        let pdf = await createPdf(
          selectedMerges[0],
          project,
          unit,
          assignment ? assignment.assignees : filteredDeal.purchasers,
          filteredDeal.pendingAddPurchasers,
          filteredDeal.pendingDeletePurchasers,
          filteredDeal.deposit,
          filteredDeal.options,
          filteredDeal.realtor[0],
          addFloorPlan && unit.floorPlan ? unit : null,
          null,
          null,
          filteredDeal,
          null,
          false,
          [],
          [],
          false,
          lease,
          assignment
        );

        let mergeTemplates = [selectedMerges[0]._id];

        if (addFloorPlan && unit.floorPlan) {
          mergeTemplates.push(unit.floorPlan!._id);
        }

        let mergeName = selectedMerges[0].name;

        const args = {
          status: 'Prepared',
          name: mergeName,
          project: project._id,
          deal: filteredDeal._id,
          // signers,
          isAPS: false,
          type: selectedMerges[0].type,
          mergeTemplate: mergeTemplates,
        };
        setDialogOpen(false);
        createDocument({ variables: { record: args, file: pdf } });
      } else if (selectedMerge.length > 1) {
        let selectedMerges = merges.filter((merge: IMerge) => selectedMerge.some((mergeName: string) => mergeName === merge.name));
        if (addFloorPlan && unit.floorPlan) {
          selectedMerges.push(unit.floorPlan);
        }
        generateMultiplePdfs(selectedMerges);
      }
    }
    setSelectedMerge([]);
  };

  const handleCloseThird = async () => {
    generateFintrac(true);
  };

  const handleCloseFourth = async () => {
    generateFintrac(true, true);
  };

  const removeDocuments = async (documentId: string, type: string) => {
    setDialogOpen(false);
    deleteDepositAmendment({ variables: { dealId: filteredDeal._id, type: type, documentId: documentId } });
  };

  const sendToExecutor = () => {
    sendDocumentToExecutor({
      variables: {
        documentId: selectedDocument!._id,
        executor,
      },
    });
    setDialogOpen(false);
  };

  const handleSendDocument = async (document: IDocuments) => {
    let dealRealtorId = null;

    sendDocument({
      variables: {
        documentId: document._id,
        dealRealtorId: dealRealtorId,
      },
    });
    setDialogOpen(false);
  };

  const dialogBox = () => {
    let removeButton = 'Cancel';
    let successButton = 'Create Document';
    let thirdOption = '';
    let fourthOption = '';
    let title = 'Please select an amendment you would like to create.';
    if (dialogType === 'execute') {
      successButton = 'Send to Executor';
      title = 'Please select an Executor to send Document';
    }
    if (dialogType === 'upload') {
      successButton = 'Upload Document';
      title = 'Upload a Document';
    }
    if (dialogType === 'download') {
      successButton = 'Download Documents';
      title = 'Select Documents';
    }
    if (dialogType === 'sendEnvelope') {
      successButton = 'Send Envelope';
      title = 'Are you sure you would like to send this envelope?';
    }
    if (dialogType === 'deleteDocument') {
      successButton = 'Delete/Void Document';
      title = 'Are you sure you would like to delete/void this document?';
    }
    if (dialogType === 'reviewAps') {
      successButton = 'Generate';
      title = 'Review APS';
    }
    if (dialogType === 'fintrac') {
      successButton = 'Create Mandatary';
      thirdOption = 'Download Fintrac';
      fourthOption = 'Create Fintrac';
      title = 'Fintrac';
    }

    return (
      <CustomDialog
        handleClose={() => setDialogOpen(false)}
        handleCloseRemove={() => setDialogOpen(false)}
        handleCloseSuccess={handleCloseSuccess}
        handleCloseThird={handleCloseThird}
        handleCloseFourth={handleCloseFourth}
        open={dialogOpen}
        removeButton={removeButton}
        thirdOption={thirdOption}
        fourthOption={fourthOption}
        successButton={successButton}
        dialogContent={dialogContent()}
        dialogTitle={title}
        maxWidth={dialogType === 'fintrac' ? 'lg' : undefined}
      />
    );
  };

  return (
    <Box
      sx={{
        '& .MuiAccordion-root': {
          border: '1px solid #000',
          padding: '4px',
        },
        '& .MuiAccordionDetails-root': {
          display: 'block',
        },
      }}
    >
      {dialogBox()}
      <FlexBetween>
        <h2 style={{ margin: 0 }}>Documents</h2>
        {user.type !== 'Developer' ? (
          <Box sx={{ mb: 1, alignSelf: 'center' }}>
            <FormControlLabel
              onChange={handleAudit}
              control={<Checkbox checked={auditDate ? true : false} color="primary" />}
              label={'RDS Accounting Review'}
            />
          </Box>
        ) : null}
      </FlexBetween>
      {filteredDeal ? (
        createDocumentLoading ||
        sendDocumentLoading ||
        dealUpdateLoading ||
        deleteDocumentLoading ||
        downloadLoading ||
        apsReviewLoading ? (
          <Box
            sx={{
              textAlign: 'center',
            }}
          >
            <LoadingLogo />
          </Box>
        ) : (
          <>
            {filteredDeal.documents.length > 0 ? (
              <div>
                {filteredDeal.documents.map((document: IDocuments, index: number) => {
                  return (
                    <React.Fragment key={index}>
                      <Accordion
                        sx={{
                          ...setBackgroundColor(document.status),
                          mb: 1,
                        }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
                          <div>
                            <h3>{document.name}</h3>
                            <p>
                              Status: <strong>{document.status}</strong>
                            </p>
                          </div>
                        </AccordionSummary>
                        <AccordionDetails>
                          <EnvelopeAccordion doc={document} sendingDocumentToExecutor={sendingDocumentToExecutor} openDialog={openDialog} />
                        </AccordionDetails>
                      </Accordion>
                    </React.Fragment>
                  );
                })}
              </div>
            ) : null}
            {accessAllowed(user, project._id, 'editDeals') &&
            !filteredDeal.cancelled.dateCancelled &&
            !filteredDeal.rescission.dateRescinded ? (
              <FlexBetween sx={{ mt: 2 }}>
                <FlexBetween>
                  <div>
                    <Button onClick={() => openDialog('create')} color="primary" variant="contained">
                      Create a Document
                    </Button>
                  </div>
                </FlexBetween>
                <FlexBetween>
                  {filteredDeal.purchasers.length ? (
                    <Button
                      sx={{ mr: 1 }}
                      onClick={() => {
                        getMerges({
                          variables: {
                            filter: {
                              OR: [
                                {
                                  type: 'Fintrac',
                                },
                                {
                                  type: 'Mandatary',
                                },
                              ],
                            },
                          },
                        });
                        openDialog('fintrac');
                      }}
                      color="primary"
                      variant="contained"
                    >
                      Fintracs/Mandatary
                    </Button>
                  ) : null}
                  {filteredDeal.purchasers.length ? (
                    <Button sx={{ mr: 1 }} onClick={() => openDialog('reviewAps')} color="primary" variant="contained">
                      Review APS
                    </Button>
                  ) : null}
                  {filteredDeal.documents.some((document: IDocuments) => document.status === 'Completed') ? (
                    <Button sx={{ mr: 1 }} onClick={() => openDialog('download')} color="primary" variant="contained">
                      Download Documents
                    </Button>
                  ) : null}
                  <Button onClick={() => openDialog('upload')} color="primary" variant="contained">
                    Upload a Document
                  </Button>
                </FlexBetween>
              </FlexBetween>
            ) : null}
          </>
        )
      ) : (
        <div>
          <em>There are no documents.</em>
        </div>
      )}
    </Box>
  );
};

const GENERATEDEPOSITDATES = gql`
  mutation generateDepositDates($dealId: MongoID!) {
    generateDepositDates(dealId: $dealId) {
      _id
      name
      project {
        _id
      }
      type
      status
      isAPS
      dsEnvelopeId
    }
  }
`;

const CREATEDOCUMENT = gql`
  mutation documentCreateOne($record: CreateOneDocumentInput, $file: Upload) {
    documentCreateOne(record: $record, file: $file) {
      _id
      name
      project {
        _id
      }
      type
      getUrl
      status
      isAPS
      dsEnvelopeId
      mergeTemplate
    }
  }
`;

const DELETEDOCUMENT = gql`
  mutation deleteDepositAmendment($type: String!, $dealId: MongoID!, $documentId: MongoID!) {
    deleteDepositAmendment(type: $type, dealId: $dealId, documentId: $documentId) {
      _id
      name
      project {
        _id
      }
      status
      isAPS
      type
      dsEnvelopeId
    }
  }
`;

const SENDDOCUMENT = gql`
  mutation documentCreateEnvelope($documentId: MongoID!, $dealRealtorId: MongoID, $templateId: String) {
    documentCreateEnvelope(documentId: $documentId, dealRealtorId: $dealRealtorId, templateId: $templateId) {
      name
      _id
      status
      dsEnvelopeId
    }
  }
`;

const SENDDOCUMENTTOEXECUTOR = gql`
  mutation documentSentToExecutor($documentId: MongoID!, $executor: executorData) {
    documentSendToExecutor(documentId: $documentId, executor: $executor) {
      _id
      status
      name
    }
  }
`;

const UPDATED_DOCUMENT = gql`
  subscription updatedDocument($projectId: String!) {
    updatedDocument(projectId: $projectId) {
      _id
      name
      status
    }
  }
`;

const UPDATEDEALINFO = gql`
  mutation dealUpdateById($_id: MongoID!, $record: UpdateByIdDealInput!) {
    dealUpdateById(_id: $_id, record: $record) {
      record {
        auditor {
          _id
          fullName
        }
        auditDate
      }
    }
  }
`;

const MERGETEMPLATES = gql`
  query mergeTemplateMany($filter: FilterFindManyMergeTemplateInput) {
    mergeTemplateMany(filter: $filter, limit: 10000) {
      _id
      project {
        _id
      }
      name
      mergeFields {
        key
        index
        pageNumber
        x
        y
        fontSize
        format
        wrap
      }
      signFields {
        key
        index
        pageNumber
        x
        y
        title
      }
      default
      totalPages
      type
      getUrl
      putUrl
    }
  }
`;

const GETDOCUMENT = gql`
  query getDocument($project: MongoID!, $document: MongoID!) {
    getDocument(project: $project, document: $document) {
      url
      name
      createdAt
      status
      completedAt
      signers {
        name
        completedAt
        status
        openedAt
      }
    }
  }
`;

const GETLEASE = gql`
  query leaseOne($filter: FilterFindOneLeaseInput) {
    leaseOne(filter: $filter) {
      _id
      active
      listing {
        _id
        dateStart
        dateEnd
        amount
        listing
        cooperating
        executor {
          _id
          fullName
        }
        developer {
          _id
          fullName
        }
        holdover
        dateAvailable
        mls
        possessionRemarks
        parking {
          space
          type
          count
        }
        sqftSource
        totalRooms {
          name
          count
          additional
        }
        washrooms {
          count
          pieces
          level
        }
        interiorFeatures
        rooms {
          level
          name
          length
          width
          height
          description1
          description2
          description3
        }
        balcony
      }
    }
  }
`;

export default Envelopes;
